import { useCallback } from 'react';
import { jsPDF } from 'jspdf';
import { parseStructuredMessage } from '../utils/helpers';
import { trackEvent } from '../utils/analytics';
import { useTranslation } from '../i18n';

export default function usePdfExport(conversation) {
  const { t } = useTranslation();

  const handleExportPdf = useCallback((messageIndex) => {
    const msg = conversation[messageIndex];
    const structuredMessage = parseStructuredMessage(msg?.fullResponse);
    const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 18;
    const bottomMargin = 20;
    const contentWidth = pageWidth - margin * 2;
    const ink = [30, 36, 40];
    const muted = [93, 104, 112];
    const accent = [18, 115, 112];
    const softAccent = [232, 245, 243];
    const panel = [249, 250, 250];
    const line = [220, 226, 230];
    let y = 22;

    const cleanText = (value) => {
      if (value === null || value === undefined) return '';

      return String(value)
        .replace(/â€™/g, "'")
        .replace(/â€˜/g, "'")
        .replace(/â€œ|â€/g, '"')
        .replace(/â€“|â€”/g, '-')
        .replace(/â€¦/g, '...')
        .replace(/Â /g, ' ')
        .replace(/Â/g, '')
        .replace(/[“”]/g, '"')
        .replace(/[‘’]/g, "'")
        .replace(/[–—]/g, '-')
        .replace(/…/g, '...')
        .replace(/[\u{1F300}-\u{1FAFF}]/gu, '')
        .replace(/[^\x09\x0A\x0D\x20-\x7E\u00A0-\u00FF]/g, '')
        .replace(/\r\n/g, '\n')
        .replace(/[ \t\f\v]+/g, ' ')
        .split('\n')
        .map((line) => line.trim())
        .join('\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
    };

    const formatScheduleText = (value) => {
      return cleanText(value)
        .replace(/\s*-\s*(?=(Manha|Manhã|Tarde|Noite|Morning|Afternoon|Evening|Night)\s*:)/gi, '\n')
        .replace(/\s+(?=(Manha|Manhã|Tarde|Noite|Morning|Afternoon|Evening|Night)\s*:)/gi, '\n')
        .replace(/^\n+/, '');
    };

    const setTextStyle = ({ bold = false, size = 10, color = ink } = {}) => {
      pdf.setFont('helvetica', bold ? 'bold' : 'normal');
      pdf.setFontSize(size);
      pdf.setTextColor(...color);
    };

    const textLines = (text, size, width, bold = false) => {
      const cleaned = cleanText(text);
      if (!cleaned) return [];
      setTextStyle({ bold, size });
      return cleaned
        .split('\n')
        .flatMap((line) => pdf.splitTextToSize(line, width));
    };

    const ensureSpace = (height = 8) => {
      if (y + height > pageHeight - bottomMargin) {
        pdf.addPage();
        y = 22;
      }
    };

    const addText = (text, options = {}) => {
      const cleaned = cleanText(text);
      if (!cleaned) return;
      const {
        bold = false,
        size = 11,
        gapAfter = 4,
        color = ink,
        x = margin,
        width = contentWidth,
        lineHeight = size * 0.48,
      } = options;

      setTextStyle({ bold, size, color });
      const lines = cleaned
        .split('\n')
        .flatMap((line) => pdf.splitTextToSize(line, width));
      lines.forEach((line) => {
        ensureSpace(lineHeight);
        pdf.text(line, x, y);
        y += lineHeight;
      });
      y += gapAfter;
    };

    const addHeader = () => {
      setTextStyle({ bold: true, size: 20, color: ink });
      pdf.text(t('share.pdfTitle'), margin, y);

      setTextStyle({ size: 9, color: muted });
      pdf.text(new Date().toLocaleDateString(), pageWidth - margin, y, { align: 'right' });

      y += 8;
      pdf.setDrawColor(...accent);
      pdf.setLineWidth(0.8);
      pdf.line(margin, y, margin + 34, y);
      pdf.setDrawColor(...line);
      pdf.setLineWidth(0.2);
      pdf.line(margin + 38, y, pageWidth - margin, y);
      y += 10;
    };

    const addSeparator = (gapBefore = 3) => {
      y += gapBefore;
      ensureSpace(10);
      pdf.setDrawColor(...line);
      pdf.setLineWidth(0.25);
      pdf.line(margin, y, pageWidth - margin, y);
      y += 9;
    };

    const drawLink = (label, url, x, linkY) => {
      if (!url) return;
      setTextStyle({ size: 9.5, color: accent });
      if (typeof pdf.textWithLink === 'function') {
        pdf.textWithLink(label, x, linkY, { url });
      } else {
        pdf.text(label, x, linkY);
        pdf.link(x, linkY - 4, pdf.getTextWidth(label), 5, { url });
      }
    };

    const addActivity = (item, itemIndex) => {
      const title = item?.title || `${t('item.activity')} ${itemIndex + 1}`;
      const description = item?.description;
      const cardX = margin;
      const cardW = contentWidth;
      const pad = 5;
      const markerW = 10;
      const textX = cardX + pad + markerW + 4;
      const textW = cardW - pad * 2 - markerW - 4;
      const titleLines = textLines(title, 10.8, textW, true);
      const descriptionLines = textLines(description, 9.3, textW, false);
      const titleLineHeight = 5.1;
      const descriptionLineHeight = 4.4;
      const hasGoogleLink = Boolean(item?.googleMapLink);
      const hasTicketLink = Boolean(item?.ticketLink);
      const linkCount = (hasGoogleLink ? 1 : 0) + (hasTicketLink ? 1 : 0);
      const cardH = Math.max(
        20,
        pad * 2 +
          titleLines.length * titleLineHeight +
          (descriptionLines.length ? 2 + descriptionLines.length * descriptionLineHeight : 0) +
          linkCount * 6.5
      );

      ensureSpace(cardH + 4);

      pdf.setDrawColor(...line);
      pdf.setFillColor(...panel);
      pdf.roundedRect(cardX, y, cardW, cardH, 2.5, 2.5, 'FD');

      pdf.setFillColor(...softAccent);
      pdf.roundedRect(cardX + pad, y + pad, markerW, 8, 2, 2, 'F');
      setTextStyle({ bold: true, size: 8, color: accent });
      pdf.text(String(itemIndex + 1), cardX + pad + markerW / 2, y + pad + 5.5, { align: 'center' });

      let innerY = y + pad + 4;
      setTextStyle({ bold: true, size: 10.8, color: ink });
      titleLines.forEach((line) => {
        pdf.text(line, textX, innerY);
        innerY += titleLineHeight;
      });

      if (descriptionLines.length) {
        innerY += 1.5;
        setTextStyle({ size: 9.3, color: muted });
        descriptionLines.forEach((line) => {
          pdf.text(line, textX, innerY);
          innerY += descriptionLineHeight;
        });
      }

      if (hasGoogleLink) {
        innerY += 1;
        drawLink(t('item.googleMaps'), item.googleMapLink, textX, innerY);
      }
      if (hasTicketLink) {
        innerY += hasGoogleLink ? 5 : 1;
        drawLink(t('item.ticket'), item.ticketLink, textX, innerY);
      }

      y += cardH + 4;
    };

    const addFooters = () => {
      const pages = pdf.getNumberOfPages();
      const website = window.location.origin;
      const generatedText = t('share.generatedAt', { website });
      for (let page = 1; page <= pages; page += 1) {
        pdf.setPage(page);
        pdf.setDrawColor(...line);
        pdf.setLineWidth(0.2);
        pdf.line(margin, pageHeight - 13, pageWidth - margin, pageHeight - 13);
        setTextStyle({ size: 8, color: muted });
        pdf.text(t('share.pdfFooter'), margin, pageHeight - 8);
        pdf.text(`${page} / ${pages}`, pageWidth - margin, pageHeight - 8, { align: 'right' });

        setTextStyle({ size: 7, color: muted });
        if (typeof pdf.textWithLink === 'function') {
          pdf.textWithLink(generatedText, margin, pageHeight - 4, { url: website });
        } else {
          pdf.text(generatedText, margin, pageHeight - 4);
          pdf.link(margin, pageHeight - 10, pdf.getTextWidth(generatedText), 5, { url: website });
        }
      }
    };

    try {
      addHeader();

      if (structuredMessage) {
        addText(structuredMessage.introduction, {
          size: 10.5,
          color: muted,
          gapAfter: 8,
          lineHeight: 5.2,
        });

        const days = Array.isArray(structuredMessage.days) ? structuredMessage.days : [];
        days.forEach((day, dayIndex) => {
          if (dayIndex > 0) addSeparator(2);

          const dayTitle = day?.title || `${t('item.day')} ${dayIndex + 1}`;
          addText(cleanText(dayTitle).toUpperCase(), {
            bold: true,
            size: 12,
            color: accent,
            gapAfter: 3,
            lineHeight: 5.8,
          });
          addText(formatScheduleText(day?.content), {
            size: 10,
            color: ink,
            gapAfter: 6,
            lineHeight: 4.9,
          });

          const dayItems = Array.isArray(day?.items) ? day.items : [];
          dayItems.forEach((item, itemIndex) => {
            addActivity(item, itemIndex);
          });
        });

        if (structuredMessage.finalization) {
          addSeparator(4);
          addText(structuredMessage.finalization, {
            size: 10.5,
            color: muted,
            gapAfter: 0,
            lineHeight: 5.2,
          });
        }
      } else {
        addText(msg?.content || msg?.message || JSON.stringify(msg || ''), {
          size: 10.5,
          lineHeight: 5.2,
          gapAfter: 0,
        });
      }

      addFooters();

      trackEvent('Export', 'pdf', `message-${messageIndex + 1}`);

      const blob = pdf.output('blob');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `voyager-message-${messageIndex + 1}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      console.error('PDF export failed:', err);
    }
  }, [conversation, t]);

  return handleExportPdf;
}
